import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import isBizBrief from '../../../Logic/Form/Sequences/Biz/isBizBrief'
import isDirectEnquiryBrief from '../../../Logic/Form/Sequences/DirectEnquiry/isDirectEnquiryBrief'
import { getLeadfeedSubmissionParams } from './getSubmissionParams/'
import { PartialBrief } from '../../../Types/Brief'
import throwGQLError from '../../../Utilities/Urql/Errors/throwGQLError'
import { useSubmitBriefMutation } from './useSubmitBrief.generated'
import getBriefUserAttributes from '../../../Utilities/User/getBriefUserAttributes'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import pushTagEvent from '../../../Utilities/GoogleTagManager/pushTagEvent'
import useSubmitBizBrief from './submitBizPlatformBrief'
import { useSpaceByIdQuery } from '../../useSpaceById'

export type BriefType = 'business' | 'enquiry' | 'leadfeed'

const getBriefTypeForSuccessPage = (brief: PartialBrief): BriefType => {
  if (isBizBrief(brief)) return 'business'

  if (isDirectEnquiryBrief(brief)) return 'enquiry'

  return 'leadfeed'
}
interface GetSuccessPagePathArgs {
  brief: PartialBrief
  businessEventUUID: string | undefined
  venueName: string | undefined
  messageLimit?: number
}

const getSuccessPagePath = ({
  brief,
  businessEventUUID,
  venueName,
  messageLimit,
}: GetSuccessPagePathArgs): string => {
  const type = getBriefTypeForSuccessPage(brief)

  const queryParams = new URLSearchParams({
    type,
    ...(venueName && { 'venue-name': venueName }),
    ...(businessEventUUID && { 'business-event-uuid': businessEventUUID }),
    ...(messageLimit && { 'message-limit': messageLimit.toString() }),
  })
  return `/success?${queryParams.toString()}`
}

interface UseSubmitBriefArgs {
  skip: boolean
  venueRequests?: { spaceId: number; hostId: number }[]
  prompt?: string
  // Awaited with the new brief id before navigating - LF favourites need an id only submission returns.
  onSubmitted?: (briefId: number) => void | Promise<void>
}
interface UseSubmitBrief {
  loading: boolean
  submitted: boolean
}

const useSubmitBrief = ({
  skip,
  venueRequests,
  prompt,
  onSubmitted,
}: UseSubmitBriefArgs): UseSubmitBrief => {
  const { brief, dispatchBrief } = useBriefContext()
  const [{ error: lfError, fetching: submittingLF, data: lfData }, submitBrief] =
    useSubmitBriefMutation()
  const [
    { error: bizPlatformError, fetching: submittingBizPlatform, data: bizPlatformData },
    submitBizPlatformBrief,
  ] = useSubmitBizBrief()

  const { spaceFetching, spaceData } = useSpaceByIdQuery(brief)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<Error>()
  const hasFired = useRef(false)

  const navigate = useNavigate()
  const { user } = useGlobalAuth()

  const submitting = !skip && (submittingLF || submittingBizPlatform || spaceFetching)

  useEffect(() => {
    if (skip || submitted || hasFired.current) return
    hasFired.current = true
    if (isBizBrief(brief)) {
      submitBizPlatformBrief(brief, venueRequests, prompt)
        .then(() => setSubmitted(true))
        .catch((error) => setSubmitError(error instanceof Error ? error : new Error(String(error))))
    } else {
      submitBrief({ params: getLeadfeedSubmissionParams(brief) })
      setSubmitted(true)
    }
  }, [skip])

  useEffect(() => {
    if (submitting || (!lfData && !bizPlatformData)) return

    const submittedBrief = lfData?.brief
    const submittedBizPlatformBrief = bizPlatformData?.createEvent

    if (!submittedBrief && !submittedBizPlatformBrief) return // this scenario would trigger the 'no response' error below

    const id = (submittedBrief && parseInt(submittedBrief?.id, 10)) || submittedBizPlatformBrief?.ID
    if (id) pushTagEvent({ ...brief, id }, 'submitBrief')

    dispatchBrief({ updateAttributes: { id } })

    const successPath = getSuccessPagePath({
      brief,
      businessEventUUID: submittedBizPlatformBrief?.UUID,
      venueName: spaceData?.spaceById?.venue?.name ?? undefined,
      messageLimit: lfData?.brief?.messageLimit || undefined,
    })

    if (isBizBrief(brief)) {
      // 'reset' the brief but leave user attrs intact, in case the user hits the back button
      dispatchBrief({
        overwrite: {
          ...getBriefUserAttributes(user, { newlyRegistered: false }),
        },
      })
    }

    void (async () => {
      // Anything needing the new brief id must run before we navigate away from the step holding it.
      if (id && onSubmitted) await onSubmitted(id)

      navigate(successPath)
    })()
  }, [lfData, bizPlatformData])

  if (lfError) throwGQLError(lfError)
  if (bizPlatformError) throwGQLError(bizPlatformError)
  if (submitted && !submitting && !lfData?.brief?.id && !bizPlatformData?.createEvent?.ID) {
    throw new Error('No response returned for brief on submission')
  }
  if (submitError) throw submitError

  return { loading: submitting, submitted }
}

export default useSubmitBrief
